import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '@/firebase/config';
import type { GeneratedDocument, DocumentTemplate, DocumentType, DocumentFormat, ID, QuoteHeaderLayout } from '@shared/types';
import { buildBaseFields, buildUpdateFields, writeAuditLog } from '@shared/utils';

const DOCS_COL = 'generated_documents';
const TMPL_COL = 'document_templates';

export interface SaveDocumentRecordInput {
  type: DocumentType;
  format: DocumentFormat;
  entityId: ID;
  templateId: ID;
  fileName: string;
  storageUrl: string;
  serialNumber: string;
}

export async function fetchDocuments(entityId: ID): Promise<GeneratedDocument[]> {
  const q = query(
    collection(db, DOCS_COL),
    where('entityId', '==', entityId),
    where('isDeleted', '==', false),
    orderBy('createdAt', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as GeneratedDocument);
}

export async function saveDocumentRecord(
  data: SaveDocumentRecordInput,
  userId: ID
): Promise<GeneratedDocument> {
  const payload = {
    ...data,
    sentAt: null,
    sentTo: null,
    ...buildBaseFields(userId),
  };
  const ref = await addDoc(collection(db, DOCS_COL), payload);
  const created = { id: ref.id, ...payload } as unknown as GeneratedDocument;
  await writeAuditLog({
    entityType: 'generated_document',
    entityId: ref.id,
    action: 'create',
    userId,
    after: data,
  });
  return created;
}

export async function recordSent(id: ID, sentTo: string, userId: ID): Promise<void> {
  const ref = doc(db, DOCS_COL, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('帳票レコードが見つかりません。');
  await updateDoc(ref, {
    sentAt: serverTimestamp(),
    sentTo,
    ...buildUpdateFields(userId),
  });
  await writeAuditLog({
    entityType: 'generated_document',
    entityId: id,
    action: 'update',
    userId,
    before: snap.data(),
    after: { sentTo },
  });
}

export async function fetchTemplates(type?: DocumentType): Promise<DocumentTemplate[]> {
  let q = query(
    collection(db, TMPL_COL),
    where('isDeleted', '==', false),
    orderBy('name')
  );
  if (type !== undefined) {
    q = query(q, where('type', '==', type));
  }
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as DocumentTemplate);
}

/**
 * 帳票生成時に適用するテンプレートを解決する。
 * 明示指定（templateId）があればそれを優先し、なければ既定（isDefault）→ 先頭 → null の順で返す。
 * これにより各帳票フォームでテンプレートを個別指定しなくても、帳票管理で指定した既定が反映される。
 */
export async function resolveDocumentTemplate(
  type: DocumentType,
  templateId?: ID | null
): Promise<DocumentTemplate | null> {
  const templates = await fetchTemplates(type);
  if (templateId) {
    const matched = templates.find((t) => t.id === templateId);
    if (matched) return matched;
  }
  return templates.find((t) => t.isDefault) ?? templates[0] ?? null;
}

export interface TemplateCreateInput {
  type: DocumentType;
  name: string;
  isDefault: boolean;
  showLogo: boolean;
  showRegistrationNumber: boolean;
  showCompanyStamp: boolean;
  showAssigneeStamp: boolean;
  showChiefStamp: boolean;
  // 「担当： （担当者名）」の表示可否
  showAssignee: boolean;
  // 自社情報の TEL / FAX の表示可否
  showTel: boolean;
  showFax: boolean;
  // 見積書ヘッダー項目のドラッグ配置（pt座標）
  headerLayout?: QuoteHeaderLayout;
}

export type TemplateUpdateInput = Partial<TemplateCreateInput>;

/**
 * 同一種別で指定ID以外のデフォルトフラグを解除し、デフォルトを常に1件に保つ。
 * これにより帳票生成時の resolveDocumentTemplate が、ユーザー指定の唯一のデフォルトを確実に返す。
 */
async function clearOtherDefaults(type: DocumentType, exceptId: ID, userId: ID): Promise<void> {
  const all = await fetchTemplates(type);
  await Promise.all(
    all
      .filter((t) => t.isDefault && t.id !== exceptId)
      .map((t) =>
        updateDoc(doc(db, TMPL_COL, t.id), { isDefault: false, ...buildUpdateFields(userId) })
      )
  );
}

export async function createTemplate(
  data: TemplateCreateInput,
  userId: ID
): Promise<DocumentTemplate> {
  const payload = { ...data, ...buildBaseFields(userId) };
  const ref = await addDoc(collection(db, TMPL_COL), payload);
  // デフォルト指定で作成した場合、他のデフォルトを解除して1件に限定する
  if (data.isDefault) await clearOtherDefaults(data.type, ref.id, userId);
  const created = { id: ref.id, ...payload } as unknown as DocumentTemplate;
  await writeAuditLog({
    entityType: 'document_template',
    entityId: ref.id,
    action: 'create',
    userId,
    after: data,
  });
  return created;
}

export async function updateTemplate(
  id: ID,
  data: TemplateUpdateInput,
  userId: ID
): Promise<DocumentTemplate> {
  const ref = doc(db, TMPL_COL, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('テンプレートが見つかりません。');
  const before = snap.data();
  const patch = { ...data, ...buildUpdateFields(userId) };
  await updateDoc(ref, patch);
  // デフォルトに設定した場合、同一種別の他のデフォルトを解除して1件に限定する
  if (data.isDefault) {
    const type = (data.type ?? (before as { type: DocumentType }).type) as DocumentType;
    await clearOtherDefaults(type, id, userId);
  }
  await writeAuditLog({
    entityType: 'document_template',
    entityId: id,
    action: 'update',
    userId,
    before,
    after: data,
  });
  return { id, ...before, ...patch } as unknown as DocumentTemplate;
}

/** テンプレートを論理削除（isDeleted=true）する */
export async function deleteTemplate(id: ID, userId: ID): Promise<void> {
  const ref = doc(db, TMPL_COL, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('テンプレートが見つかりません。');
  await updateDoc(ref, { isDeleted: true, ...buildUpdateFields(userId) });
  await writeAuditLog({
    entityType: 'document_template',
    entityId: id,
    action: 'delete',
    userId,
    before: snap.data(),
  });
}
